// src/pages/LevelTemplate.jsx
// ✅ V3 - Timer now from global PracticeTimerProvider (no per-level restart)
// ✅ Time expired during song? Let them finish, THEN show paywall
// ✅ V4 - Progress tracking: recordSession on completion, sessionStartRef, sessionWrongRef
import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import VirtualPiano, {
  normalizeNoteName as vpNormalizeNoteName,
} from "../components/VirtualPiano";
import RealPianoListener from "../components/RealPianoListener";
import Confetti from "react-confetti";

import StarQuestBar from "../components/StarQuestBar";
import StarUnlockModal from "../components/StarUnlockModal";

import FloatingPianoOverlay from "../components/FloatingPianoOverlay";
import StreamVideo from "../components/StreamVideo";

import LevelSidebarNav from "../components/LevelSidebarNav";
import { supabase } from "../supabaseClient";
import { TrophyStorage } from "../services/trophyStorage";
import TrebleTownMapModal from "../components/TrebleTownMapModal";

// ✅ V4: Progress tracking
import { recordSession } from "../utils/progressTracker";

function isProbablyIPad() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/i.test(ua);
  const iPadLike = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadLike;
}

function ensureRandomUUIDPolyfill() {
  if (typeof window === "undefined") return;
  try {
    if (!window.crypto) return;
    if (typeof window.crypto.randomUUID === "function") return;
    window.crypto.randomUUID = () => {
      const t = Date.now().toString(16);
      const r = Math.random().toString(16).slice(2);
      return `pwp_${t}_${r}`.slice(0, 36);
    };
  } catch {}
}

const FLAT_TO_SHARP = { DB:"C#", EB:"D#", GB:"F#", AB:"G#", BB:"A#", CB:"B", FB:"E" };
const EDGE_ENHARMONICS = { "E#":"F", "B#":"C" };
const MIDI_TO_PC = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];

const STARQUEST_STORAGE_KEY = "tt_starquest_completed_levels_v1";
const PIANO_UI_PREFS_KEY    = "tt_piano_ui_prefs_v1";
const KEYGLOW_PREF_KEY      = "pwp_show_key_glow_pref_v1";

const STAR_IMAGES_BOTTOM_TO_TOP = [
  "/images/stars/star-zebra.png",
  "/images/stars/star-penguin.png",
  "/images/stars/star-Fish.png",
  "/images/stars/star-Ren.png",
  "/images/stars/star-spider.png",
  "/images/stars/star-panda.png",
  "/images/stars/star-Peacock.png",
  "/images/stars/star-croc.png",
];

const TOTAL_STAR_SLOTS  = STAR_IMAGES_BOTTOM_TO_TOP.length;
const TOTAL_LEVEL_SLOTS = 10;
const ZEBRA_STAR_IMAGE  = "/images/stars/star-zebra.png";
const REWARD_CHIME      = "/audio/reward-chime.mp3";
const STAR_DING         = "/audio/star-ding.mp3";
const ALL_STARS_STINGER = "/audio/fur-elise-stinger.mp3";
const ASSET_V           = "2";
const DORON_SLEEPING    = `/images/sleeping-doron.png?v=${ASSET_V}`;
const DORON_AWAKE       = `/images/DORON.png?v=${ASSET_V}`;

const MIC_UI_THROTTLE_MS       = 90;
const MIC_PREV_NOTE_NEUTRAL_MS = 1200;
const MIC_NO_PROGRESS_HINT_MS  = 3500;
const MIC_LOCK_TOOLTIP         = "Mic disabled — restart level to play on real piano.";
const MIC_DETECTION_SENSITIVITY    = 1.25;
const MIC_TOLERANCE_CENTS          = 85;
const MIC_TOLERANCE_HZ             = 14;
const MIC_WRONG_STABLE_FRAMES      = 1;
const MIC_WRONG_COOLDOWN_MS        = 180;
const MIC_WRONG_CENTS_THRESHOLD    = 70;
const MIC_WRONG_HZ_THRESHOLD       = 7;
const CONFETTI_DURATION_MS         = 4500;
const CONFETTI_PIECES              = 400;

function safePlayAudio(src) {
  if (!src || typeof window === "undefined") return;
  try { const a = new Audio(src); a.volume = 0.9; a.play().catch(() => {}); } catch {}
}

function sleep(ms) { return new Promise((res) => setTimeout(res, ms)); }

function playAudioInstanceAndWait(audio, timeoutMs = 4500) {
  return new Promise((resolve) => {
    if (!audio) return resolve(false);
    let finished = false;
    const done = () => { if (finished) return; finished = true; cleanup(); resolve(true); };
    const cleanup = () => {
      try { audio.removeEventListener("ended", done); audio.removeEventListener("error", done); } catch {}
    };
    try {
      audio.addEventListener("ended", done);
      audio.addEventListener("error", done);
      const t = setTimeout(() => { try { cleanup(); } catch {} resolve(false); }, timeoutMs);
      const p = audio.play();
      if (p && typeof p.catch === "function") { p.catch(() => { clearTimeout(t); done(); }); }
    } catch { done(); }
  });
}

function prewarmAudio(src) {
  if (!src || typeof window === "undefined") return null;
  try {
    const a = new Audio(src); a.volume = 0;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => { try { a.pause(); a.currentTime = 0; } catch {} }).catch(() => {});
    } else { try { a.pause(); a.currentTime = 0; } catch {} }
    return a;
  } catch { return null; }
}

function flyToTarget(fromEl, toEl, { onDone } = {}) {
  if (!fromEl || !toEl || typeof window === "undefined") { onDone?.(); return; }
  const a = fromEl.getBoundingClientRect();
  const b = toEl.getBoundingClientRect();
  const flyer = fromEl.cloneNode(true);
  Object.assign(flyer.style, {
    position:"fixed", left:`${a.left}px`, top:`${a.top}px`,
    width:`${a.width}px`, height:`${a.height}px`,
    zIndex:"10000", pointerEvents:"none", margin:"0",
  });
  document.body.appendChild(flyer);
  const dx = (b.left+b.width/2) - (a.left+a.width/2);
  const dy = (b.top+b.height/2) - (a.top+a.height/2);
  const anim = flyer.animate(
    [{ transform:"translate(0px,0px) scale(1)", opacity:1 },
     { transform:`translate(${dx}px,${dy}px) scale(0.45)`, opacity:1 }],
    { duration:650, easing:"cubic-bezier(.2,.9,.2,1)" }
  );
  anim.onfinish = () => { try { flyer.remove(); } catch {} onDone?.(); };
}

function loadPianoUIPrefs() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PIANO_UI_PREFS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    return {
      mode: obj.mode === "floating" ? "floating" : "docked",
      size: "l",
      x: Number.isFinite(obj.x) ? obj.x : null,
      y: Number.isFinite(obj.y) ? obj.y : null,
    };
  } catch { return null; }
}

function savePianoUIPrefs({ mode, x, y }) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PIANO_UI_PREFS_KEY, JSON.stringify({
      mode: mode === "floating" ? "floating" : "docked",
      size: "l",
      x: Number.isFinite(x) ? x : undefined,
      y: Number.isFinite(y) ? y : undefined,
    }));
  } catch {}
}

function loadKeyGlowPref() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEYGLOW_PREF_KEY);
    if (raw == null) return null;
    if (raw === "1") return true;
    if (raw === "0") return false;
    const parsed = JSON.parse(raw);
    if (typeof parsed === "boolean") return parsed;
    return null;
  } catch { return null; }
}

function saveKeyGlowPref(v) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(KEYGLOW_PREF_KEY, v ? "1" : "0"); } catch {}
}

function normalizeGameNoteSafe(note) {
  if (!note) return "";
  let raw = String(note).trim().toUpperCase()
    .replaceAll("♯","#").replaceAll("＃","#").replaceAll("♭","B");
  const m = raw.match(/[A-G](#|B)?/);
  if (!m) return "";
  let pc = m[0];
  if (EDGE_ENHARMONICS[pc]) pc = EDGE_ENHARMONICS[pc];
  if (pc.length === 2 && pc[1] === "B") pc = FLAT_TO_SHARP[pc] || pc;
  return vpNormalizeNoteName ? vpNormalizeNoteName(pc) : pc;
}

export function normalizeIncomingNote(any) {
  if (any == null) return "";
  if (Array.isArray(any)) return normalizeIncomingNote(any[any.length-1] ?? any[0]);
  if (typeof any === "number" && Number.isFinite(any)) {
    return normalizeGameNoteSafe(MIDI_TO_PC[((Math.round(any)%12)+12)%12]);
  }
  if (typeof any === "object" && any.name) return normalizeGameNoteSafe(any.name);
  return normalizeGameNoteSafe(String(any).split(/[/,|]/)[0]);
}

export function notesMatch(a, b) {
  const na = normalizeIncomingNote(a);
  const nb = normalizeIncomingNote(b);
  return na && nb && na === nb;
}

export function computeProgressFromMatched(lines, matched, total) {
  const clamped = Math.max(0, Math.min(matched, total));
  let remaining = clamped;
  for (let i = 0; i < lines.length; i++) {
    const len = lines[i]?.notes?.length ?? 0;
    if (remaining < len) return { line:i, note:remaining };
    remaining -= len;
  }
  const last = Math.max(0, lines.length-1);
  return { line:last, note:lines[last]?.notes?.length ?? 0 };
}

function loadCompletedLevels() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STARQUEST_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((n) => typeof n==="number" && Number.isFinite(n)).sort((a,b)=>a-b);
  } catch { return []; }
}

function saveCompletedLevels(levels) {
  if (typeof window === "undefined") return;
  try {
    const unique = Array.from(new Set(levels)).sort((a,b)=>a-b);
    window.localStorage.setItem(STARQUEST_STORAGE_KEY, JSON.stringify(unique));
  } catch {}
}

function markLevelCompleted(levelId) {
  if (typeof window === "undefined") return [];
  const current = loadCompletedLevels();
  if (!current.includes(levelId)) {
    const updated = [...current, levelId];
    saveCompletedLevels(updated);
    return updated;
  }
  return current;
}

function getDoronStatusImage(currentLevel, isComplete, completedLevels, explicitSrc) {
  if (explicitSrc) return explicitSrc;
  const hasLevel4 = completedLevels?.includes(4);
  if (currentLevel === 2 || currentLevel === 3) return DORON_SLEEPING;
  if (currentLevel === 4) return (hasLevel4 || isComplete) ? DORON_SLEEPING : DORON_AWAKE;
  if (currentLevel > 1) return DORON_SLEEPING;
  return DORON_AWAKE;
}

function PillButton({ children, className="", ...rest }) {
  return (
    <button type="button"
      className={"px-4 py-1.5 rounded-full bg-[#FFC94E] border-[3px] border-[#456EFF] shadow-[0_6px_0_#E0862B] " +
        "text-[10px] sm:text-xs md:text-sm font-extrabold uppercase text-slate-900 active:translate-y-[2px] " +
        "active:shadow-[0_4px_0_#E0862B] transition-transform " + className}
      {...rest}
    >{children}</button>
  );
}

function ConfirmModal({ open, title="Are you sure?", body="This action cannot be undone.", confirmLabel="Yes", cancelLabel="No", onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-[28px] bg-white shadow-2xl border border-white/90 px-6 py-5 sm:px-8 sm:py-6 text-center">
        <div className="text-3xl mb-2">↺</div>
        <h3 className="text-lg sm:text-xl font-black text-slate-900">{title}</h3>
        <p className="mt-2 text-sm text-slate-600">{body}</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <button type="button" onClick={onConfirm}
            className="px-6 py-3 rounded-full bg-[#EF4444] text-white font-extrabold shadow-[0_10px_0_rgba(185,28,28,0.18)] hover:brightness-[1.03] active:translate-y-[1px] transition">
            {confirmLabel}
          </button>
          <button type="button" onClick={onCancel}
            className="px-6 py-3 rounded-full bg-white border-2 border-slate-200 text-slate-800 font-extrabold shadow-sm hover:bg-slate-50 active:translate-y-[1px] transition">
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function MobileProgressSection({ currentLevel, completedLevels, starImages, totalLevels=TOTAL_LEVEL_SLOTS, show }) {
  if (!show) return null;
  const completedSet = new Set(completedLevels || []);
  const starsCollected = Math.max(0, Math.min((completedLevels||[]).length, starImages.length));
  return (
    <div className="block md:hidden mt-4 w-full max-w-5xl mx-auto">
      <div className="rounded-[22px] bg-white/95 border border-slate-100 shadow-md px-3 py-3">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Progress</div>
        <div className="flex flex-col gap-3">
          <div>
            <div className="text-[10px] font-extrabold text-slate-600 mb-1.5">Levels</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {Array.from({length:totalLevels},(_,idx) => {
                const level = idx+1;
                const isCurrent = level === Number(currentLevel);
                const isDone = completedSet.has(level);
                let cls = "min-w-[34px] h-9 rounded-full flex items-center justify-center text-xs font-black border ";
                if (isCurrent) cls += "bg-[#F9A8D4] border-[#F472B6] text-slate-900 shadow-sm";
                else if (isDone) cls += "bg-[#DCFCE7] border-[#86EFAC] text-slate-900";
                else cls += "bg-slate-50 border-slate-200 text-slate-400";
                return <div key={level} className={cls} aria-label={`Level ${level}`}>{level}</div>;
              })}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="text-[10px] font-extrabold text-slate-600">Stars</div>
              <div className="text-[10px] font-black text-slate-700">{starsCollected}/{starImages.length}</div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {starImages.map((src,idx) => {
                const collected = idx < starsCollected;
                return (
                  <div key={`${src}-${idx}`}
                    className={"min-w-[40px] h-[40px] rounded-full flex items-center justify-center border " +
                      (collected ? "bg-[#FEF3C7] border-[#FBBF24] shadow-sm" : "bg-slate-50 border-slate-200")}>
                    {collected
                      ? <img src={src} alt="" className="w-6 h-6 object-contain" />
                      : <span className="text-slate-300 text-lg">☆</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TopNav({ session, onLogin, onLogout, onGo }) {
  return (
    <header className="w-full bg-white/95 shadow-sm">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex items-center">
        <button type="button" onClick={() => onGo("/")}
          className="text-base sm:text-lg font-extrabold text-[#1D4ED8] hover:opacity-90" aria-label="Go to Home">
          Piano With Pan
        </button>
        <div className="flex-1" />
        <nav className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-sm font-semibold text-slate-700">
          <button type="button" className="hover:text-[#4F46E5]" onClick={() => onGo("/pricing")}>PRICING</button>
          <button type="button" className="hover:text-[#4F46E5] hidden sm:inline" onClick={() => onGo("/parents")}>PARENTS</button>
          <button type="button" className="hover:text-[#4F46E5]" onClick={() => onGo("/store")}>STORE</button>
          <button type="button" className="hover:text-[#4F46E5] hidden sm:inline" onClick={() => onGo("/about")}>ABOUT&nbsp;US</button>
          {session ? (
            <button type="button" onClick={onLogout}
              className="px-2.5 sm:px-3 py-1 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50" title="Log out">
              LOG OUT
            </button>
          ) : (
            <button type="button" onClick={onLogin}
              className="px-2.5 sm:px-3 py-1 rounded-full border border-slate-300 text-[#1D4ED8] hover:bg-slate-50" title="Log in">
              LOG IN
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

function RightMenu({ currentLevel, completedLevels, doronStatusImageSrc, starImages, getSlotEl, newlyEarnedBottomIndex }) {
  return (
    <aside className="hidden md:flex flex-col items-center pt-2 pb-4 w-28">
      {currentLevel > 1 && (
        <div className="-mt-1 w-full flex justify-center">
          <StarQuestBar
            completedLevels={completedLevels}
            doronImageSrc={doronStatusImageSrc}
            zebraStarImageSrc={ZEBRA_STAR_IMAGE}
            starImages={starImages}
            getSlotEl={getSlotEl}
            newlyEarnedBottomIndex={newlyEarnedBottomIndex}
            className="w-full origin-top scale-95"
          />
        </div>
      )}
      <div className="flex-1" />
      <img src="/images/Wis_.png" className="h-20 object-contain drop-shadow" alt="" />
    </aside>
  );
}

function LevelScene({ videoSrc, onStart, startLabel="Start Game" }) {
  const [videoError, setVideoError] = useState(false);
  const isStream = typeof videoSrc === "string" && videoSrc.startsWith("stream:");
  const streamId = isStream ? videoSrc.replace(/^stream:/,"").trim() : null;
  const hasVideo = Boolean(videoSrc) && !videoError && (!isStream || Boolean(streamId));
  return (
    <div className="flex-1 flex flex-col px-3 sm:px-4 pb-6 pt-3">
      <div className="flex-1 flex flex-col items-center gap-4 sm:gap-5 justify-center sm:justify-start">
        <div className="relative w-full max-w-3xl mx-auto">
          <div className="absolute inset-0 blur-xl bg-gradient-to-tr from-[#7BD5FF] via-[#A78BFA] to-[#FFCF71] opacity-70 pointer-events-none" />
          <div className="relative z-[1] aspect-video rounded-[24px] sm:rounded-[32px] shadow-[0_20px_40px_rgba(15,23,42,0.4)] overflow-hidden bg-slate-900">
            {hasVideo ? (
              isStream ? (
                <div className="w-full h-full"><StreamVideo videoId={streamId} title="Level Intro" className="w-full h-full" /></div>
              ) : (
                <video className="w-full h-full object-cover" src={videoSrc} controls playsInline controlsList="nodownload" onError={() => setVideoError(true)} />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-900/80">
                <div className="text-center px-4">
                  <div className="text-white font-extrabold text-sm sm:text-base">Video not found</div>
                  <div className="text-white/70 text-[10px] sm:text-xs mt-1">Check your Stream ID or videoSrc path.</div>
                </div>
              </div>
            )}
          </div>
        </div>
        <PillButton className="px-10 sm:px-16 py-3 text-xs sm:text-sm" onClick={onStart}>{startLabel}</PillButton>
      </div>
    </div>
  );
}

function DefaultHero({ backgroundImage, levelId, isComplete, noteEvent, matchedCount, totalNotes, heroImageFit="cover" }) {
  return (
    <div className="relative w-full max-w-3xl mx-auto rounded-[22px] sm:rounded-[24px] shadow bg-slate-900/5 overflow-hidden">
      {backgroundImage && (
        <div className="w-full bg-slate-900/5">
          <img src={backgroundImage} alt=""
            className={heroImageFit==="contain" ? "w-full h-48 sm:h-72 object-contain bg-[#FFF7ED]" : "w-full h-36 sm:h-56 object-cover"} />
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="bg-white/85 rounded-2xl px-4 sm:px-5 py-3 sm:py-4 shadow-xl border border-white/80 backdrop-blur text-center pointer-events-auto">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Level {levelId}</div>
          <div className="text-xs sm:text-lg font-black text-slate-900">
            {isComplete ? "Song Complete!" : "Play the melody to fill the magic bar."}
          </div>
          <div className="mt-1 text-[9px] sm:text-[10px] text-slate-600">
            {matchedCount}/{totalNotes} notes matched •{" "}
            {noteEvent==="hit" ? "Nice!" : noteEvent==="miss" ? "Wrong note!" : "Follow the notes below."}
          </div>
        </div>
      </div>
    </div>
  );
}

function MelodyBoard({
  levelMelodyLines, lyricGroupsPerLine, noteGroupIndexesPerLine,
  progress, isComplete, hasStarted, isListening, currentNote, matchedCount,
  onStartPlay, onStopListening, wrongFlash, onMicToggle, noteEvent,
  micHintActive, micLocked, micLockedTooltip, showPreview, previewPlaying, onPreviewToggle,
}) {
  const lineIndex = progress.line;
  const line = levelMelodyLines?.[lineIndex];
  const detectorDisplay = currentNote ? normalizeIncomingNote(currentNote) || "—" : "—";
  const TOTAL = (levelMelodyLines||[]).reduce((s,l)=>s+(l?.notes?.length??0),0);
  const done = Math.min(matchedCount, TOTAL);
  const pct  = TOTAL ? Math.max(0, Math.min(100, (done/TOTAL)*100)) : 0;

  if (!line) {
    return (
      <div className="mt-1 w-full max-w-4xl mx-auto bg-white rounded-3xl shadow-md px-3 sm:px-4 py-3 border border-slate-100">
        <div className="text-center text-xs text-slate-600">Melody data missing for this line.</div>
      </div>
    );
  }

  return (
    <div id="tt-level-melody" className="mt-1 w-full max-w-4xl mx-auto bg-white rounded-3xl shadow-md px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-100">
      <div className="flex flex-col items-center">
        {!hasStarted && (
          <div className="mb-2 flex flex-col items-center gap-1">
            <button onClick={onStartPlay}
              className="px-8 sm:px-10 py-2.5 sm:py-3 rounded-full bg-[#22C55E] text-white shadow-[0_6px_0_#16A34A] text-xs sm:text-lg font-black tracking-wide animate-pulse"
              style={{ touchAction:"manipulation" }}>
              🎤 Play Now
            </button>
            {showPreview && (
              <button type="button" onClick={onPreviewToggle}
                className={"mt-1 px-4 py-1.5 rounded-full border text-[10px] sm:text-xs font-extrabold uppercase shadow-sm transition " +
                  (previewPlaying ? "bg-indigo-100 border-indigo-300 text-indigo-800" : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100")}
                style={{ touchAction:"manipulation" }}>
                {previewPlaying ? "⏸ Stop Preview" : "▶️ Preview Song"}
              </button>
            )}
            <div className="text-[9px] sm:text-[10px] text-slate-600">Turn on your microphone or use the virtual piano.</div>
          </div>
        )}

        <div className="flex flex-col gap-1 items-center">
          <div className="flex justify-center gap-4 sm:gap-8 md:gap-12 flex-wrap">
            {(noteGroupIndexesPerLine?.[lineIndex]||[]).map((group, idx) => {
              const lyrics = (lyricGroupsPerLine?.[lineIndex]||[])[idx] || "";
              const notesLocal = line.notes.slice(group.start, group.end+1);
              return (
                <div key={idx} className="flex flex-col items-center gap-1">
                  <div className="flex gap-1 sm:gap-1.5">
                    {notesLocal.map((note, localIdx) => {
                      const globalIndex = group.start + localIdx;
                      let status = "pending";
                      const curLine = progress.line, curNote = progress.note;
                      if (isComplete) status = "done";
                      else if (lineIndex < curLine) status = "done";
                      else if (lineIndex > curLine) status = "pending";
                      else {
                        if (globalIndex < curNote) status = "done";
                        else if (globalIndex === curNote) status = wrongFlash ? "wrong" : "current";
                      }
                      const isHintBox = Boolean(micHintActive) && Boolean(isListening) && status==="current" && !wrongFlash;
                      const base = "relative w-9 h-9 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center font-semibold " +
                        "text-[10px] sm:text-sm transition-all max-[380px]:w-8 max-[380px]:h-8";
                      let stateClass = "bg-slate-100 text-slate-700";
                      if (status==="done")    stateClass = "bg-[#6EE7B7] text-slate-900 tt-right-flash";
                      if (status==="current") stateClass = "bg-white border-2 border-[#4F46E5] text-[#4F46E5] tt-current-pulse";
                      if (status==="wrong")   stateClass = "bg-red-500 text-white border-2 border-red-600 scale-110";
                      const hintClass = isHintBox ? "ring-4 ring-amber-300 ring-offset-2 animate-pulse" : "";
                      return (
                        <div key={globalIndex} className={`${base} ${stateClass} ${hintClass}`}>
                          {isHintBox && <span className="absolute -top-5 sm:-top-6 text-[14px] sm:text-base animate-bounce">⬇️</span>}
                          {note}
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[12px] sm:text-[11px] md:text-xs text-slate-700 whitespace-nowrap font-semibold">{lyrics}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-2 w-full flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex flex-col">
              <span className="uppercase tracking-[0.18em] text-[8px] text-slate-500">Line {progress.line+1} / {levelMelodyLines.length}</span>
              <span className="text-[9px] text-slate-400">Detector</span>
            </div>
            <div className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#EEF2FF] border-2 border-[#4F46E5] text-[#4F46E5] font-extrabold text-sm sm:text-md shadow-sm">
              {detectorDisplay}
            </div>
            <div className={"inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-xl border-2 font-extrabold text-sm sm:text-base shadow-sm " +
              (noteEvent==="miss" ? "bg-red-100 border-red-300 text-red-700" : "bg-slate-50 border-slate-200 text-slate-400")}
              title={noteEvent==="miss" ? "Incorrect note" : "No wrong note"}
              aria-label={noteEvent==="miss" ? "Incorrect note" : "No wrong note"}>
              {noteEvent==="miss" ? "X" : "—"}
            </div>
            {hasStarted && (
              <button type="button" onClick={micLocked ? undefined : onMicToggle} disabled={micLocked}
                className={"ml-1 inline-flex items-center gap-1 rounded-full px-2.5 sm:px-3 py-1 border text-[9px] sm:text-[10px] font-extrabold uppercase shadow-sm transition " +
                  (micLocked ? "bg-slate-100 text-slate-400 border-slate-200 opacity-80 cursor-not-allowed" :
                   isListening ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                   "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200")}
                style={{ touchAction:"manipulation" }}
                title={micLocked ? micLockedTooltip : "Toggle microphone for real piano"}
                aria-label={micLocked ? micLockedTooltip : "Toggle microphone for real piano"}>
                {micLocked ? "🎤 MIC: LOCKED" : isListening ? "🎤 MIC: ON" : "🎤 MIC: OFF"}
              </button>
            )}
          </div>
          {hasStarted && isListening && (
            <button onClick={onStopListening}
              className="px-3 sm:px-4 py-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold shadow"
              style={{ touchAction:"manipulation" }}>
              Stop
            </button>
          )}
        </div>

        <div className="mt-1 w-full">
          <div className="h-2.5 sm:h-3 rounded-full bg-slate-200 overflow-hidden border border-slate-100">
            <div className={"h-full rounded-full transition-[width] duration-200 ease-out " + (isComplete ? "bg-emerald-400" : "bg-[#4F46E5]")}
              style={{ width:`${pct}%` }} aria-label={`Magic progress ${done} of ${TOTAL}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LevelTemplate({
  levelId = 1,
  videoSrc,
  melodyLines = [],
  lyricGroupsPerLine = [],
  noteGroupIndexesPerLine = [],
  backgroundImage,
  renderHero,
  onComplete,
  currentLevelForSidebar = levelId,
  completionTitle = "You finished the song!",
  completionBody = <>Nice work!</>,
  heroImageFit = "cover",
  doronStatusImageSrc = null,
  startLabel = "Start Game",
  onStartGame = null,
  mobileMeterBelowPiano = false,
  firstKeyOverride = null,
  lastKeyOverride = null,
  completedHeroImageSrc = null,
  onNextLevel = null,
  enableFloatingPiano = true,
  defaultPianoMode = "docked",
  showPianoModeToggle = true,
  previewAudioSrc = null,
}) {
  const navigate = useNavigate();
  const isIPad = useMemo(() => isProbablyIPad(), []);
  const ENABLE_CONFETTI = useMemo(() => !isIPad, [isIPad]);

  useEffect(() => { ensureRandomUUIDPolyfill(); }, []);

  const [session, setSession] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!alive) return;
        setSession(data?.session || null);
      } catch { if (!alive) return; setSession(null); }
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => { setSession(sess || null); });
    return () => { alive = false; try { sub?.subscription?.unsubscribe?.(); } catch {} };
  }, []);

  const handleNavGo = useCallback((path) => {
    navigate(path);
    try { window.scrollTo({ top:0, behavior:"smooth" }); } catch {}
  }, [navigate]);
  const handleLogin  = useCallback(() => { navigate("/login"); }, [navigate]);
  const handleLogout = useCallback(async () => {
    try { await supabase.auth.signOut(); } catch {}
    navigate("/");
  }, [navigate]);

  const TOTAL_NOTES = useMemo(() => (melodyLines||[]).reduce((s,l)=>s+(l?.notes?.length??0),0), [melodyLines]);
  const flatMelody  = useMemo(() => (melodyLines||[]).flatMap((l)=>l?.notes??[]), [melodyLines]);

  const [view,         setView        ] = useState(videoSrc ? "scene" : "melody");
  const [matchedCount, setMatchedCount] = useState(0);
  const [isComplete,   setIsComplete  ] = useState(false);
  const [hasStarted,   setHasStarted  ] = useState(false);
  const [isListening,  setIsListening ] = useState(false);
  const [lastHeardNote,setLastHeardNote] = useState(null);
  const [noteEvent,    setNoteEvent   ] = useState(null);
  const [wrongFlash,   setWrongFlash  ] = useState(false);
  const [uiToast,      setUiToast     ] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiRunId,setConfettiRunId] = useState(0);
  const [showKeyGlow,  setShowKeyGlow ] = useState(() => {
    const saved = loadKeyGlowPref();
    if (typeof saved === "boolean") return saved;
    return Number(levelId) <= 3;
  });
  const [completedLevels, setCompletedLevels] = useState(() =>
    typeof window !== "undefined" ? loadCompletedLevels() : []
  );
  const [rewardOpen,              setRewardOpen             ] = useState(false);
  const [showMap,                 setShowMap                ] = useState(false);
  const [rewardCollected,         setRewardCollected        ] = useState(false);
  const [newlyEarnedBottomIndex,  setNewlyEarnedBottomIndex ] = useState(null);
  const [suppressCompletionOverlay,setSuppressCompletionOverlay] = useState(false);
  const [micHintActive,           setMicHintActive          ] = useState(false);
  const [micLocked,               setMicLocked              ] = useState(false);
  const [restartConfirmOpen,      setRestartConfirmOpen     ] = useState(false);
  const [previewPlaying,          setPreviewPlaying         ] = useState(false);

  // ✅ V4 progress tracking refs
  const sessionStartRef    = useRef(0);
  const sessionRecordedRef = useRef(false);
  const sessionWrongRef    = useRef(0);

  const wrongFlashTimeoutRef = useRef(null);
  const toastTimeoutRef      = useRef(null);
  const celebrationFiredRef  = useRef(false);
  const confettiTimeoutRef   = useRef(null);
  const collectingRef        = useRef(false);
  const lastMatchAtRef       = useRef(0);
  const prevMatchedPcRef     = useRef(null);
  const prevMatchedAtRef     = useRef(0);
  const micHintTimeoutRef    = useRef(null);
  const lastProgressAtRef    = useRef(0);
  const matchedCountRef      = useRef(matchedCount);
  const totalNotesRef        = useRef(TOTAL_NOTES);
  const flatMelodyRef        = useRef(flatMelody);
  const isCompleteRef        = useRef(isComplete);
  const isListeningRef       = useRef(isListening);
  const viewRef              = useRef(view);
  const previewAudioRef      = useRef(null);
  const slotElsRef           = useRef({});
  const detectorLastUiAtRef  = useRef(0);
  const detectorLastPcRef    = useRef(null);

  useEffect(() => { matchedCountRef.current = matchedCount; }, [matchedCount]);
  useEffect(() => { totalNotesRef.current   = TOTAL_NOTES;  }, [TOTAL_NOTES]);
  useEffect(() => { flatMelodyRef.current   = flatMelody;   }, [flatMelody]);
  useEffect(() => { isCompleteRef.current   = isComplete;   }, [isComplete]);
  useEffect(() => { isListeningRef.current  = isListening;  }, [isListening]);
  useEffect(() => { viewRef.current         = view;         }, [view]);
  useEffect(() => { saveKeyGlowPref(showKeyGlow); }, [showKeyGlow]);

  const trophyCount = useMemo(() => {
    try {
      const r = TrophyStorage?.getRounds?.() || TrophyStorage?.rounds || TrophyStorage?.getAll?.() || [];
      return Array.isArray(r) ? r.length : 0;
    } catch { return 0; }
  }, []);

  const stopPreview = useCallback(() => {
    try { const a = previewAudioRef.current; if (a) { a.pause(); a.currentTime = 0; } } catch {}
    setPreviewPlaying(false);
  }, []);

  const togglePreview = useCallback(() => {
    if (!previewAudioSrc || hasStarted) return;
    if (previewPlaying) { stopPreview(); return; }
    try {
      const a = new Audio(previewAudioSrc);
      a.volume = 0.95;
      a.onended = () => setPreviewPlaying(false);
      a.onerror = () => setPreviewPlaying(false);
      previewAudioRef.current = a;
      const p = a.play();
      setPreviewPlaying(true);
      if (p && typeof p.catch === "function") p.catch(() => setPreviewPlaying(false));
    } catch { setPreviewPlaying(false); }
  }, [previewAudioSrc, hasStarted, previewPlaying, stopPreview]);

  useEffect(() => { stopPreview(); }, [previewAudioSrc, stopPreview]);

  const setDetectorNoteThrottled = useCallback((pc) => {
    if (!pc) return;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (detectorLastPcRef.current===pc && now-detectorLastUiAtRef.current < MIC_UI_THROTTLE_MS) return;
    detectorLastPcRef.current = pc;
    detectorLastUiAtRef.current = now;
    setLastHeardNote(pc);
  }, []);

  const progress = useMemo(
    () => computeProgressFromMatched(melodyLines, matchedCount, TOTAL_NOTES),
    [melodyLines, matchedCount, TOTAL_NOTES]
  );

  const expectedKey = useMemo(() => {
    if (view !== "melody" || isComplete || !melodyLines?.length) return null;
    return `${progress.line}-${progress.note}`;
  }, [view, isComplete, melodyLines, progress.line, progress.note]);

  const wrongGateRef = useRef({ key:null, used:false });
  useEffect(() => {
    if (!expectedKey) return;
    if (wrongGateRef.current.key !== expectedKey) {
      wrongGateRef.current = { key:expectedKey, used:false };
      setNoteEvent(null);
    }
  }, [expectedKey]);

  const doronImageToUse = useMemo(
    () => getDoronStatusImage(currentLevelForSidebar, isComplete, completedLevels, doronStatusImageSrc),
    [currentLevelForSidebar, isComplete, completedLevels, doronStatusImageSrc]
  );

  const showToast = useCallback((msg) => {
    setUiToast(msg || "");
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => { setUiToast(""); toastTimeoutRef.current = null; }, 1400);
  }, []);

  const triggerWrongFlash = useCallback(() => {
    setWrongFlash(true);
    if (wrongFlashTimeoutRef.current) clearTimeout(wrongFlashTimeoutRef.current);
    wrongFlashTimeoutRef.current = setTimeout(() => { setWrongFlash(false); wrongFlashTimeoutRef.current = null; }, 240);
  }, []);

  const registerWrongOnce = useCallback(() => {
    if (view !== "melody" || isComplete) return;
    if (!expectedKey) return;
    if (wrongGateRef.current.key === expectedKey && wrongGateRef.current.used) return;
    wrongGateRef.current = { key:expectedKey, used:true };
    setNoteEvent("miss");
    sessionWrongRef.current += 1; // ✅ V4: track wrong notes
    triggerWrongFlash();
  }, [view, isComplete, expectedKey, triggerWrongFlash]);

  const registerWrongOnceRef = useRef(registerWrongOnce);
  useEffect(() => { registerWrongOnceRef.current = registerWrongOnce; }, [registerWrongOnce]);

  useEffect(() => {
    return () => {
      if (wrongFlashTimeoutRef.current)  clearTimeout(wrongFlashTimeoutRef.current);
      if (toastTimeoutRef.current)       clearTimeout(toastTimeoutRef.current);
      if (micHintTimeoutRef.current)     clearTimeout(micHintTimeoutRef.current);
      if (confettiTimeoutRef.current)    clearTimeout(confettiTimeoutRef.current);
      stopPreview();
    };
  }, [stopPreview]);

  const fireConfettiOnce = useCallback(() => {
    if (!ENABLE_CONFETTI || celebrationFiredRef.current) return;
    celebrationFiredRef.current = true;
    if (confettiTimeoutRef.current) { clearTimeout(confettiTimeoutRef.current); confettiTimeoutRef.current = null; }
    setConfettiRunId((n) => n+1);
    setShowConfetti(true);
    confettiTimeoutRef.current = setTimeout(() => { setShowConfetti(false); confettiTimeoutRef.current = null; }, CONFETTI_DURATION_MS);
  }, [ENABLE_CONFETTI]);

  useEffect(() => {
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    lastProgressAtRef.current = now;
    setMicHintActive(false);
  }, [matchedCount]);

  useEffect(() => {
    if (micHintTimeoutRef.current) { clearTimeout(micHintTimeoutRef.current); micHintTimeoutRef.current = null; }
    if (!hasStarted || !isListening || view !== "melody" || isComplete) { setMicHintActive(false); return; }
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (!lastProgressAtRef.current) lastProgressAtRef.current = now;
    const startCount = matchedCountRef.current;
    const elapsed = now - (lastProgressAtRef.current || now);
    const delay = Math.max(80, MIC_NO_PROGRESS_HINT_MS - elapsed);
    micHintTimeoutRef.current = setTimeout(() => {
      if (!isListeningRef.current || viewRef.current !== "melody" || isCompleteRef.current || !hasStarted) return;
      if (matchedCountRef.current !== startCount) return;
      setMicHintActive(true);
    }, delay);
    return () => { if (micHintTimeoutRef.current) { clearTimeout(micHintTimeoutRef.current); micHintTimeoutRef.current = null; } };
  }, [hasStarted, isListening, view, isComplete, matchedCount]);

  useEffect(() => {
    if (!hasStarted || view !== "melody" || isComplete || celebrationFiredRef.current) return;
    if (progress.line >= 2) fireConfettiOnce();
  }, [hasStarted, view, isComplete, progress.line, fireConfettiOnce]);

  const starBottomIndexForThisLevel = useMemo(() => {
    const raw = levelId-2;
    return Math.max(0, Math.min(raw, STAR_IMAGES_BOTTOM_TO_TOP.length-1));
  }, [levelId]);

  const starImageForThisLevel = useMemo(() => {
    if (levelId === 2) return ZEBRA_STAR_IMAGE;
    return STAR_IMAGES_BOTTOM_TO_TOP[starBottomIndexForThisLevel] || ZEBRA_STAR_IMAGE;
  }, [levelId, starBottomIndexForThisLevel]);

  // ✅ Song completion — confetti + star reward + V4 session recording
  useEffect(() => {
    if (!isComplete && matchedCount >= TOTAL_NOTES && TOTAL_NOTES > 0) {
      setIsComplete(true);
      onComplete?.();
      if (!celebrationFiredRef.current) fireConfettiOnce();

      // ── V4: Record session on first completion only ──────────────────
      if (!sessionRecordedRef.current) {
        sessionRecordedRef.current = true;
        try {
          recordSession({
            source:       "level",
            levelId,
            levelName:    String(completionTitle || `Level ${levelId}`)
              .replace(/^You finished\s+/i,"")
              .replace(/!$/,"")
              .trim() || `Level ${levelId}`,
            totalNotes:   TOTAL_NOTES,
            correctNotes: TOTAL_NOTES,
            wrongNotes:   sessionWrongRef.current,
            durationMs:   sessionStartRef.current ? Date.now()-sessionStartRef.current : 0,
            stars:        null,
          });
        } catch {}
      }
      // ─────────────────────────────────────────────────────────────────

      if (levelId >= 2) {
        const alreadyCompleted = completedLevels.includes(levelId);
        if (!alreadyCompleted) {
          collectingRef.current = false;
          setRewardCollected(false);
          setRewardOpen(true);
          setSuppressCompletionOverlay(true);
        }
      }
    }
  }, [matchedCount, TOTAL_NOTES, isComplete, onComplete, levelId, completedLevels, fireConfettiOnce, completionTitle]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.querySelectorAll(".tt-current-key-glow").forEach((el) => el.classList.remove("tt-current-key-glow"));
    if (!showKeyGlow || view !== "melody" || isComplete) return;
    const line = melodyLines[progress.line];
    if (!line) return;
    const expectedRaw = line.notes[progress.note];
    if (!expectedRaw) return;
    let elems = Array.from(document.querySelectorAll(`[data-note="${String(expectedRaw)}"]`));
    if (elems.length === 0) {
      const pc = normalizeIncomingNote(expectedRaw);
      if (pc) elems = Array.from(document.querySelectorAll(`[data-note^="${pc}"]`));
    }
    elems.forEach((el) => el.classList.add("tt-current-key-glow"));
  }, [showKeyGlow, view, isComplete, melodyLines, progress.line, progress.note]);

  const toggleMic = useCallback(() => {
    const btn = document.querySelector("#level-mic-control button");
    if (btn) btn.click();
    setIsListening((p) => !p);
  }, []);

  const handleStartPlay = useCallback(() => {
    stopPreview();
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    lastProgressAtRef.current = now;
    setMicHintActive(false);

    // ✅ V4: Capture session start on first play
    if (!hasStarted && !sessionStartRef.current) sessionStartRef.current = Date.now();

    if (!hasStarted) setHasStarted(true);
    if (micLocked) { showToast(MIC_LOCK_TOOLTIP); return; }
    if (!isListening) toggleMic();
  }, [hasStarted, isListening, toggleMic, micLocked, showToast, stopPreview]);

  const handleStopListening = useCallback(() => { if (isListening) toggleMic(); }, [isListening, toggleMic]);

  const handleMicToggle = useCallback(() => {
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    lastProgressAtRef.current = now;
    setMicHintActive(false);

    // ✅ V4: Capture session start on first mic enable
    if (!hasStarted && !sessionStartRef.current) sessionStartRef.current = Date.now();

    if (!hasStarted) setHasStarted(true);
    if (micLocked) { showToast(MIC_LOCK_TOOLTIP); return; }
    toggleMic();
  }, [hasStarted, toggleMic, micLocked, showToast]);

  const handleVirtualKey = useCallback((noteName) => {
    if (!hasStarted) {
      // ✅ V4: Capture session start on first key tap
      if (!sessionStartRef.current) sessionStartRef.current = Date.now();
      setHasStarted(true);
    }
    if (isComplete) return;
    stopPreview();
    if (isListening) { toggleMic(); setMicLocked(true); showToast("Switched to Virtual Piano ✅ (Mic locked until Restart)"); }
    const pc = normalizeIncomingNote(noteName);
    setDetectorNoteThrottled(pc);
    setMatchedCount((prev) => {
      if (prev >= TOTAL_NOTES) return prev;
      const { line, note } = computeProgressFromMatched(melodyLines, prev, TOTAL_NOTES);
      const expected = melodyLines?.[line]?.notes?.[note];
      if (!expected || !notesMatch(pc, expected)) { registerWrongOnce(); return prev; }
      setNoteEvent("hit");
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      lastProgressAtRef.current = now;
      setMicHintActive(false);
      const expectedPc = normalizeIncomingNote(expected);
      prevMatchedPcRef.current = expectedPc || pc || null;
      prevMatchedAtRef.current = now;
      lastMatchAtRef.current = now;
      return prev+1;
    });
  }, [hasStarted, isComplete, isListening, toggleMic, showToast, melodyLines, TOTAL_NOTES, registerWrongOnce, setDetectorNoteThrottled, stopPreview]);

  const resetLevel = useCallback(() => {
    setMatchedCount(0); setIsComplete(false); setHasStarted(false);
    setLastHeardNote(null); setNoteEvent(null); setWrongFlash(false);
    celebrationFiredRef.current = false;
    setShowConfetti(false); setConfettiRunId(0);
    if (confettiTimeoutRef.current) { clearTimeout(confettiTimeoutRef.current); confettiTimeoutRef.current = null; }
    wrongGateRef.current = { key:null, used:false };
    lastMatchAtRef.current = 0;
    setMicHintActive(false);
    lastProgressAtRef.current = 0;
    prevMatchedPcRef.current = null;
    prevMatchedAtRef.current = 0;
    setMicLocked(false); setRestartConfirmOpen(false);
    if (micHintTimeoutRef.current) { clearTimeout(micHintTimeoutRef.current); micHintTimeoutRef.current = null; }
    detectorLastUiAtRef.current = 0; detectorLastPcRef.current = null;
    stopPreview();
    if (isListening) { try { const btn = document.querySelector("#level-mic-control button"); if (btn) btn.click(); } catch {} }
    setIsListening(false);
    setUiToast("");
    setRewardOpen(false); setRewardCollected(false);
    setNewlyEarnedBottomIndex(null); setSuppressCompletionOverlay(false);
    collectingRef.current = false;
    // ✅ V4: Reset session tracking for next play
    sessionStartRef.current    = 0;
    sessionRecordedRef.current = false;
    sessionWrongRef.current    = 0;
  }, [isListening, stopPreview]);

  const outerScale = useMemo(() => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1024;
    if (w >= 1024) return 0.9;
    if (w < 360) return 0.94;
    if (w < 420) return 0.96;
    if (w < 520) return 0.98;
    return 1;
  }, []);

  const hero = renderHero ? renderHero({
    levelId, isComplete, noteEvent, matchedCount, totalNotes:TOTAL_NOTES, lastHeardNote, backgroundImage, heroImageFit,
  }) : (
    <DefaultHero backgroundImage={backgroundImage} levelId={levelId} isComplete={isComplete}
      noteEvent={noteEvent} matchedCount={matchedCount} totalNotes={TOTAL_NOTES} heroImageFit={heroImageFit} />
  );

  const [windowSize, setWindowSize] = useState({
    width:  typeof window !== "undefined" ? window.innerWidth  : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    let last = { w:window.innerWidth, h:window.innerHeight };
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = window.innerWidth, h = window.innerHeight;
        if (w !== last.w || Math.abs(h-last.h) > 120) { last = {w,h}; setWindowSize({width:w,height:h}); }
      });
    };
    window.addEventListener("resize", onResize);
    return () => { if (raf) cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, []);

  const mobilePianoWidthBoost = useMemo(() => {
    const w = windowSize.width, lvl = Number(levelId)||0;
    const isWideRange = lvl >= 8 && lvl <= 9;
    if (w >= 768) return 1;
    if (w < 520) return isWideRange ? 1.06 : 1.15;
    return isWideRange ? 1.02 : 1.06;
  }, [windowSize.width, levelId]);

  const pianoConfig = useMemo(() => {
    const w = windowSize.width;
    let base;
    if      (w < 350) base = { keyWidth:22, whiteKeyHeight:96,  blackKeyWidth:14, blackKeyHeight:58  };
    else if (w < 390) base = { keyWidth:24, whiteKeyHeight:104, blackKeyWidth:16, blackKeyHeight:64  };
    else if (w < 480) base = { keyWidth:28, whiteKeyHeight:118, blackKeyWidth:19, blackKeyHeight:76  };
    else if (w < 768) base = { keyWidth:38, whiteKeyHeight:150, blackKeyWidth:24, blackKeyHeight:96  };
    else if (w < 1024)base = { keyWidth:50, whiteKeyHeight:170, blackKeyWidth:30, blackKeyHeight:110 };
    else              base = { keyWidth:62, whiteKeyHeight:200, blackKeyWidth:36, blackKeyHeight:128 };
    return { ...base, keyWidth:Math.round(base.keyWidth*mobilePianoWidthBoost), blackKeyWidth:Math.round(base.blackKeyWidth*mobilePianoWidthBoost) };
  }, [windowSize.width, mobilePianoWidthBoost]);

  const pianoPreset = useMemo(() => {
    const baseRange = (Number(levelId) >= 8 && Number(levelId) <= 9) ? { first:"B3", last:"E5" } : { first:"C4", last:"C5" };
    const first = firstKeyOverride || baseRange.first;
    const last  = lastKeyOverride  || baseRange.last;
    const showAllBlack = Number(levelId) >= 3;
    const blackLabels  = Number(levelId) === 2 ? { "A#":"A#","A#4":"A#","A#3":"A#" } : {};
    return { firstKey:first, lastKey:last, showAllBlackLabels:Number(levelId)===1 ? false : showAllBlack, blackKeyLabels:blackLabels };
  }, [levelId, firstKeyOverride, lastKeyOverride]);

  const firstKey = pianoPreset.firstKey;
  const lastKey  = pianoPreset.lastKey;

  const baseVirtualPianoScale = useMemo(() => {
    const w = windowSize.width;
    if (w >= 768) return 1;
    if (w < 350) return 0.92;
    if (w < 390) return 0.95;
    if (w < 430) return 0.97;
    return 1;
  }, [windowSize.width]);

  const pianoScaleBoost = useMemo(() => {
    const w = windowSize.width, lvl = Number(levelId)||0;
    const isWideRange = lvl >= 8 && lvl <= 9;
    if (w < 520) return isWideRange ? 1.04 : 1.15;
    if (w < 768) return isWideRange ? 1.03 : 1.07;
    return 1;
  }, [windowSize.width, levelId]);

  const virtualPianoScale = useMemo(() => Math.min(1.22, baseVirtualPianoScale*pianoScaleBoost), [baseVirtualPianoScale, pianoScaleBoost]);

  const pianoContainerMinHeight = useMemo(() => {
    const base = pianoConfig.whiteKeyHeight || 160;
    return Math.ceil(base*(virtualPianoScale||1)+56);
  }, [pianoConfig.whiteKeyHeight, virtualPianoScale]);

  const handleCollectStar = useCallback((starEl) => {
    if (collectingRef.current) return;
    collectingRef.current = true;
    const before = loadCompletedLevels();
    const willCompleteAll = !before.includes(levelId) && before.length === TOTAL_STAR_SLOTS-1;
    const stingerAudio = willCompleteAll ? prewarmAudio(ALL_STARS_STINGER) : null;
    safePlayAudio(REWARD_CHIME);
    const targetEl = slotElsRef.current?.[starBottomIndexForThisLevel];
    flyToTarget(starEl, targetEl, {
      onDone: async () => {
        safePlayAudio(STAR_DING);
        const updated = markLevelCompleted(levelId);
        setCompletedLevels(updated);
        setNewlyEarnedBottomIndex(starBottomIndexForThisLevel);
        setTimeout(() => setNewlyEarnedBottomIndex(null), 1400);
        const allStarsNow = (updated?.length||0) >= TOTAL_STAR_SLOTS;
        if (allStarsNow) {
          try {
            await sleep(320);
            if (stingerAudio) {
              try { stingerAudio.volume = 0.9; stingerAudio.currentTime = 0; } catch {}
              await playAudioInstanceAndWait(stingerAudio, 4500);
            } else {
              const a = new Audio(ALL_STARS_STINGER); a.volume = 0.9;
              await playAudioInstanceAndWait(a, 4500);
            }
          } catch {} finally { setRewardCollected(true); collectingRef.current = false; }
          return;
        }
        setRewardCollected(true); collectingRef.current = false;
      },
    });
  }, [levelId, starBottomIndexForThisLevel]);

  const goToNextLevel = useCallback(() => {
    if (typeof onNextLevel === "function") { onNextLevel(levelId); return; }
    navigate(`/level-${Number(levelId)+1}`);
  }, [onNextLevel, levelId, navigate]);

  const handleNextFromReward = useCallback(() => { setRewardOpen(false); goToNextLevel(); }, [goToNextLevel]);

  const initialPianoPrefs = useMemo(() => {
    const saved = loadPianoUIPrefs();
    return saved || { mode:defaultPianoMode==="floating" ? "floating" : "docked", size:"l", x:null, y:null };
  }, [defaultPianoMode]);

  const [pianoMode,     setPianoMode    ] = useState(enableFloatingPiano ? initialPianoPrefs.mode : "docked");
  const [floatingPrefs, setFloatingPrefs] = useState(() => ({
    size:"l",
    x: Number.isFinite(initialPianoPrefs.x) ? initialPianoPrefs.x : null,
    y: Number.isFinite(initialPianoPrefs.y) ? initialPianoPrefs.y : null,
  }));

  useEffect(() => {
    if (!enableFloatingPiano) return;
    savePianoUIPrefs({ mode:pianoMode, x:floatingPrefs.x, y:floatingPrefs.y });
  }, [enableFloatingPiano, pianoMode, floatingPrefs.x, floatingPrefs.y]);

  const goDocked   = useCallback(() => setPianoMode("docked"), []);
  const goFloating = useCallback(() => { if (!enableFloatingPiano) return; setPianoMode("floating"); }, [enableFloatingPiano]);

  const handleMicNoteDetected = useCallback((note) => {
    const pc = normalizeIncomingNote(note);
    if (!pc) return;
    setDetectorNoteThrottled(pc);
  }, [setDetectorNoteThrottled]);

  const handleMicWrongConfirmed = useCallback((info) => {
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const detectedPc = info?.detected ? normalizeIncomingNote(info.detected) : null;
    if (detectedPc) setDetectorNoteThrottled(detectedPc);
    if (!isListeningRef.current || viewRef.current !== "melody" || isCompleteRef.current) return;
    const prevPc = prevMatchedPcRef.current;
    const prevAt = prevMatchedAtRef.current || lastMatchAtRef.current || 0;
    if (prevPc && detectedPc && detectedPc===prevPc && prevAt && now-prevAt < MIC_PREV_NOTE_NEUTRAL_MS) return;
    registerWrongOnceRef.current?.();
  }, [setDetectorNoteThrottled]);

  const handleMicNoteMatched = useCallback((arr) => {
    if (!Array.isArray(arr)) return;
    const total = totalNotesRef.current || 0;
    const len = Math.min(arr.length, total);
    setMatchedCount((prev) => {
      if (len <= prev) return prev;
      setNoteEvent("hit");
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      lastMatchAtRef.current = now;
      lastProgressAtRef.current = now;
      setMicHintActive(false);
      const justMatchedRaw = flatMelodyRef.current?.[len-1] ?? arr[len-1];
      prevMatchedPcRef.current = normalizeIncomingNote(justMatchedRaw) || null;
      prevMatchedAtRef.current = now;
      return len;
    });
  }, []);

  const doronImageForBar = doronImageToUse;
  const nextLabel = completedLevels.length >= TOTAL_STAR_SLOTS ? "Final Battle Unlocked →" : "Next Level Unlocked →";
  const isWideRangeLevel = useMemo(() => { const lvl = Number(levelId)||0; return lvl >= 8 && lvl <= 9; }, [levelId]);
  const allowMobileNudgeScroll = windowSize.width < 520 && Boolean(isWideRangeLevel);
  const canPreview   = Boolean(previewAudioSrc) && !hasStarted && view === "melody";
  const isPlayMode   = hasStarted && !isComplete;
  const isMobilePortrait = windowSize.width < 768 && windowSize.height >= windowSize.width;

  return (
    <div className={"bg-gradient-to-b from-[#9BE7FF] via-[#FDF7FF] to-[#FFE8C7] flex flex-col " +
      "min-h-[100dvh] overflow-x-hidden " +
      "pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"}>
      <TopNav session={session} onLogin={handleLogin} onLogout={handleLogout} onGo={handleNavGo} />

      {uiToast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9999]">
          <div className="px-3 py-1.5 rounded-full bg-slate-900 text-white text-[11px] sm:text-xs font-semibold shadow-lg">{uiToast}</div>
        </div>
      )}

      {showConfetti && ENABLE_CONFETTI && (
        <div className="fixed inset-0 z-[9998] pointer-events-none">
          <Confetti key={confettiRunId} width={windowSize.width} height={windowSize.height} numberOfPieces={CONFETTI_PIECES} recycle={false} />
        </div>
      )}

      <StarUnlockModal
        open={rewardOpen}
        starImageSrc={starImageForThisLevel}
        completedHeroImageSrc={completedHeroImageSrc || backgroundImage || null}
        title="STAR UNLOCKED!"
        completionTitle={completionTitle}
        completionBody={completionBody}
        onCollect={(starEl) => handleCollectStar(starEl)}
        canGoNext={rewardCollected}
        onNext={handleNextFromReward}
        nextLabel={nextLabel}
      />

      <ConfirmModal
        open={restartConfirmOpen}
        title="Restart Level?"
        body="Your current progress in this song will be cleared."
        confirmLabel="Yes, Restart"
        cancelLabel="No, Keep Playing"
        onConfirm={resetLevel}
        onCancel={() => setRestartConfirmOpen(false)}
      />

      <div className="flex-1 flex justify-center">
        <div className="max-w-7xl mx-auto w-full px-2 sm:px-3 py-3 sm:py-4">
          <div className="flex gap-3 sm:gap-4 justify-center"
            style={{ transform:`scale(${outerScale})`, transformOrigin:"top center" }}>
            <LevelSidebarNav currentLevel={currentLevelForSidebar} />

            <div className="flex-1 bg-white/80 rounded-[26px] sm:rounded-[32px] shadow-2xl border border-white/80 flex flex-col overflow-hidden">
              {view === "scene" ? (
                <LevelScene videoSrc={videoSrc} startLabel={startLabel}
                  onStart={() => {
                    setView("melody");
                    window.scrollTo({ top:0, behavior:"smooth" });
                    if (typeof onStartGame === "function") onStartGame();
                  }} />
              ) : (
                <div className="flex-1 flex flex-col px-3 sm:px-4 pb-4 sm:pb-5 pt-2.5 sm:pt-3 relative">
                  <div className="flex flex-col sm:flex-row items-center sm:items-stretch justify-center gap-3 sm:gap-5">
                    <div className="flex-1 flex justify-center w-full">{hero}</div>
                  </div>

                  <MelodyBoard
                    levelMelodyLines={melodyLines} lyricGroupsPerLine={lyricGroupsPerLine}
                    noteGroupIndexesPerLine={noteGroupIndexesPerLine} progress={progress}
                    isComplete={isComplete} hasStarted={hasStarted} isListening={isListening}
                    currentNote={lastHeardNote} matchedCount={matchedCount}
                    onStartPlay={handleStartPlay} onStopListening={handleStopListening}
                    wrongFlash={wrongFlash} onMicToggle={handleMicToggle} noteEvent={noteEvent}
                    micHintActive={micHintActive} micLocked={micLocked} micLockedTooltip={MIC_LOCK_TOOLTIP}
                    showPreview={canPreview} previewPlaying={previewPlaying} onPreviewToggle={togglePreview}
                  />

                  <div className="mt-2 w-full flex flex-col items-center justify-center">
                    <div className="w-full max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-2 mb-2">
                      <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] sm:text-xs text-slate-600">
                        <input type="checkbox" className="h-3 w-3 sm:h-4 sm:w-4 accent-[#3B82F6]"
                          checked={showKeyGlow} onChange={(e) => setShowKeyGlow(e.target.checked)} />
                        <span>Show key glow (hint)</span>
                      </label>
                      {enableFloatingPiano && showPianoModeToggle && (
                        <>
                          <span className="mx-1 text-slate-300">|</span>
                          <PillButton className={"px-3 py-1 text-[9px] sm:text-[10px] " + (pianoMode==="docked" ? "opacity-100" : "opacity-70")} onClick={goDocked}>Docked</PillButton>
                          <PillButton className={"px-3 py-1 text-[9px] sm:text-[10px] " + (pianoMode==="floating" ? "opacity-100" : "opacity-70")} onClick={goFloating}>Floating</PillButton>
                        </>
                      )}
                    </div>

                    {pianoMode === "docked" && (
                      <div className="w-full max-w-full sm:max-w-5xl flex justify-center relative">
                        {!hasStarted && (
                          <div className="absolute inset-0 z-10 bg-white/60 rounded-3xl flex items-center justify-center">
                            <span className="text-xs text-slate-600">Tap <strong>Play Now</strong> to begin.</span>
                          </div>
                        )}
                        <div className={"bg-white rounded-[22px] sm:rounded-[26px] shadow-lg border border-slate-100 " +
                          "px-1.5 sm:px-3 py-4 sm:py-6 lg:py-7 w-full flex justify-center items-center " +
                          (!hasStarted ? "opacity-80 pointer-events-none" : "") +
                          (allowMobileNudgeScroll ? " overflow-x-auto" : " overflow-hidden")}
                          style={{ minHeight:pianoContainerMinHeight }}>
                          <div className={"w-full flex " + (allowMobileNudgeScroll ? "justify-start" : "justify-center")}
                            style={{ transform:`scale(${virtualPianoScale})`, transformOrigin:allowMobileNudgeScroll ? "top left" : "top center" }}>
                            <VirtualPiano
                              onNoteClick={handleVirtualKey}
                              keyWidth={pianoConfig.keyWidth} whiteKeyHeight={pianoConfig.whiteKeyHeight}
                              blackKeyWidth={pianoConfig.blackKeyWidth} blackKeyHeight={pianoConfig.blackKeyHeight}
                              firstKey={firstKey} lastKey={lastKey}
                              showAllBlackLabels={pianoPreset.showAllBlackLabels}
                              blackKeyLabels={pianoPreset.blackKeyLabels}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {pianoMode === "floating" && enableFloatingPiano && (
                      <div className="w-full max-w-5xl mx-auto">
                        <div className="text-center text-[10px] sm:text-xs text-slate-500">Piano is floating ✅ (it will snap to bottom corners/center)</div>
                      </div>
                    )}

                    {hasStarted && isListening && (
                      <button onClick={handleStopListening}
                        className="mt-2 px-4 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold">
                        Stop Listening
                      </button>
                    )}

                    <div id="level-mic-control" className="w-full flex justify-center">
                      <RealPianoListener
                        targetMelody={flatMelody} startLabel="" stopLabel=""
                        showMicHint={false} buttonWrapperClassName="hidden" containerClassName="hidden"
                        onNoteDetected={handleMicNoteDetected} onWrongConfirmed={handleMicWrongConfirmed}
                        onNoteMatched={handleMicNoteMatched} onComplete={() => setMatchedCount(TOTAL_NOTES)}
                        detectionSensitivity={MIC_DETECTION_SENSITIVITY} toleranceCents={MIC_TOLERANCE_CENTS}
                        toleranceHz={MIC_TOLERANCE_HZ} wrongStableFramesRequired={MIC_WRONG_STABLE_FRAMES}
                        wrongCooldownMs={MIC_WRONG_COOLDOWN_MS} wrongCentsThreshold={MIC_WRONG_CENTS_THRESHOLD}
                        wrongHzThreshold={MIC_WRONG_HZ_THRESHOLD}
                      />
                    </div>

                    {!isPlayMode && (
                      <div className="mt-1 w-full max-w-5xl mx-auto flex flex-col sm:flex-row items-stretch justify-end gap-3">
                        <div className="flex-1 flex justify-center sm:justify-end">
                          <div className="flex flex-wrap justify-center sm:justify-end gap-2 sm:gap-3 text-[10px] sm:text-xs text-slate-700">
                            <PillButton className="px-3 py-1 text-[9px] sm:text-[10px] flex items-center gap-1" onClick={() => handleNavGo("/help")}>❓ Help</PillButton>
                            <PillButton className="px-3 py-1 text-[9px] sm:text-[10px] flex items-center gap-1" onClick={() => handleNavGo("/trophy-cabinet")} title="Your Trophy Wall">
                              🏆 Trophies{trophyCount > 0 ? ` (${trophyCount})` : ""}
                            </PillButton>
                            <PillButton className="px-3 py-1 text-[9px] sm:text-[10px] flex items-center gap-1" onClick={() => handleNavGo("/song-book")} title="All unlocked songs">🎵 Song Book</PillButton>
                            <PillButton className="px-3 py-1 text-[9px] sm:text-[10px] flex items-center gap-1" onClick={() => setShowMap(true)} title="Treble Town adventure map">🗺️ Map</PillButton>
                            <PillButton className="px-3 py-1 text-[9px] sm:text-[10px] flex items-center gap-1" onClick={() => handleNavGo("/progress")} title="Parent progress report">📊 Progress</PillButton>
                          </div>
                        </div>
                      </div>
                    )}

                    <MobileProgressSection currentLevel={currentLevelForSidebar} completedLevels={completedLevels}
                      starImages={STAR_IMAGES_BOTTOM_TO_TOP} show={isMobilePortrait && !isPlayMode} />
                  </div>

                  <div className="mt-2 flex justify-end text-[11px] sm:text-xs text-slate-600">
                    <button type="button" onClick={() => setRestartConfirmOpen(true)}
                      className="underline underline-offset-2 decoration-dashed hover:text-[#4F46E5]">
                      Restart level
                    </button>
                  </div>

                  {isComplete && !rewardOpen && !suppressCompletionOverlay && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="pointer-events-auto bg-white/95 rounded-[28px] shadow-2xl px-6 py-4 sm:px-10 sm:py-6 flex flex-col items-center gap-3 max-w-md text-center">
                        <div className="text-3xl sm:text-4xl">✨</div>
                        <h2 className="text-lg sm:text-xl font-black text-slate-900">{completionTitle}</h2>
                        <p className="text-xs sm:text-sm text-slate-600">{completionBody}</p>
                        <div className="flex flex-wrap justify-center gap-3 mt-1">
                          {Number(levelId) >= 2 && Number(levelId) < 10 && (
                            <button type="button" onClick={goToNextLevel}
                              className="px-6 sm:px-8 py-3 rounded-full bg-[#7C3AED] text-white font-extrabold shadow-[0_10px_0_rgba(88,28,135,0.25)] hover:brightness-[1.03] active:translate-y-[1px] transition">
                              Next Level
                            </button>
                          )}
                          <button type="button" onClick={() => { resetLevel(); setView("melody"); window.scrollTo({top:0,behavior:"smooth"}); }}
                            className="px-6 sm:px-8 py-3 rounded-full bg-white border-2 border-slate-200 text-slate-800 font-extrabold shadow-sm hover:bg-slate-50 active:translate-y-[1px] transition">
                            Play Again
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <RightMenu
              currentLevel={currentLevelForSidebar}
              completedLevels={completedLevels}
              doronStatusImageSrc={doronImageForBar}
              starImages={STAR_IMAGES_BOTTOM_TO_TOP}
              newlyEarnedBottomIndex={newlyEarnedBottomIndex}
              getSlotEl={(bottomIndex, el) => { if (el) slotElsRef.current[bottomIndex] = el; }}
            />
          </div>
        </div>
      </div>

      <TrebleTownMapModal open={showMap} onClose={() => setShowMap(false)} />

      <FloatingPianoOverlay
        open={enableFloatingPiano && view === "melody" && pianoMode === "floating"}
        windowSize={windowSize} hasStarted={hasStarted} levelId={levelId}
        firstKey={firstKey} lastKey={lastKey} pianoConfig={pianoConfig}
        onNoteClick={handleVirtualKey} showKeyGlow={showKeyGlow}
        onToggleKeyGlow={(v) => setShowKeyGlow(v)} onDock={goDocked}
        initialPrefs={floatingPrefs} onPrefsChange={setFloatingPrefs}
        showAllBlackLabels={pianoPreset.showAllBlackLabels} blackKeyLabels={pianoPreset.blackKeyLabels}
      />

      {/* ✅ V6: Level 3 completion → Premium upsell modal
          Shown when a free user navigates to Level 4+ from the completion screen.
          Triggered by ProtectedLevelRoute redirect — this modal is NOT shown here.
          Keeping this block as a placeholder comment for future in-level upsell triggers. */}
    </div>
  );
}